// Peta Global export helpers.
// Renders a slippy-tile map (satellite / OSM / hybrid) + customer pins onto a
// canvas so it can be downloaded as JPG or PDF, independent of Leaflet's
// current viewport size and free of CORS-tainted canvas issues (tiles are
// fetched through the backend `/api/map-tile-proxy` endpoint).

const TILE_SIZE = 256;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Slippy tile ↔ pixel math (Web Mercator, XYZ scheme, tile size 256).
function lonToPixelX(lon, zoom) {
  return ((lon + 180) / 360) * TILE_SIZE * Math.pow(2, zoom);
}
function latToPixelY(lat, zoom) {
  const rad = (lat * Math.PI) / 180;
  return (
    (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) *
    0.5 *
    TILE_SIZE *
    Math.pow(2, zoom)
  );
}

// Build the tile URL for a given base layer.
// Mirrors PetaGlobal.jsx's baseLayers.
export function buildTileUrl(base, x, y, z) {
  if (base === "street") {
    const sub = "abc"[(x + y) % 3];
    return `https://${sub}.tile.openstreetmap.org/${z}/${x}/${y}.png`;
  }
  const sub = String((x + y) % 4); // 0-3
  const layer = base === "hybrid" ? "y" : "s";
  return `https://mt${sub}.google.com/vt/lyrs=${layer}&x=${x}&y=${y}&z=${z}`;
}

// Fetch a single tile through the backend proxy (CORS-safe).
function fetchTileImage(tileUrl) {
  return new Promise((resolve) => {
    const proxied = `${API}/map-tile-proxy?url=${encodeURIComponent(tileUrl)}`;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = proxied;
  });
}

// Given desired output pixel size and lat/lng bounds, find the tile zoom
// that best fits within maxSide pixels. Also clamp to baseMaxZoom.
export function chooseFitZoom(bounds, maxSide, baseMaxZoom = 20) {
  for (let z = baseMaxZoom; z >= 1; z--) {
    const nwX = lonToPixelX(bounds.west, z);
    const nwY = latToPixelY(bounds.north, z);
    const seX = lonToPixelX(bounds.east, z);
    const seY = latToPixelY(bounds.south, z);
    const w = Math.abs(seX - nwX);
    const h = Math.abs(seY - nwY);
    if (Math.max(w, h) <= maxSide) return z;
  }
  return 1;
}

// Round-rect helper (canvas 2d).
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// Compute lat/lng bounds that cover a list of {lat, lng} points, with a small
// padding fraction on each side so pins near the edge aren't clipped.
export function computePointsBounds(points, padFrac = 0.06) {
  if (!points.length) return null;
  let n = -Infinity, s = Infinity, e = -Infinity, w = Infinity;
  for (const p of points) {
    if (p.lat > n) n = p.lat;
    if (p.lat < s) s = p.lat;
    if (p.lng > e) e = p.lng;
    if (p.lng < w) w = p.lng;
  }
  // Guard against a single-point bounds (would zoom to infinity).
  if (n === s) { n += 0.0005; s -= 0.0005; }
  if (e === w) { e += 0.0005; w -= 0.0005; }
  const padLat = (n - s) * padFrac;
  const padLng = (e - w) * padFrac;
  return { north: n + padLat, south: s - padLat, east: e + padLng, west: w - padLng };
}

// Main renderer. Returns an HTMLCanvasElement.
//   bounds:     { north, south, east, west }
//   zoom:       integer tile zoom (higher = more tiles, more detail)
//   base:       'satellite' | 'street' | 'hybrid'
//   points:     [{ cid, lat, lng, color }]
//   showIdLabels: boolean
//   onProgress: optional (done, total) callback
export async function renderMapExport({
  bounds,
  zoom,
  base = "satellite",
  points = [],
  showIdLabels = true,
  onProgress = null,
}) {
  const nwX = lonToPixelX(bounds.west, zoom);
  const nwY = latToPixelY(bounds.north, zoom);
  const seX = lonToPixelX(bounds.east, zoom);
  const seY = latToPixelY(bounds.south, zoom);

  const outW = Math.max(1, Math.round(seX - nwX));
  const outH = Math.max(1, Math.round(seY - nwY));

  const startTileX = Math.floor(nwX / TILE_SIZE);
  const startTileY = Math.floor(nwY / TILE_SIZE);
  const endTileX = Math.floor((seX - 0.0001) / TILE_SIZE);
  const endTileY = Math.floor((seY - 0.0001) / TILE_SIZE);
  const offsetX = nwX - startTileX * TILE_SIZE;
  const offsetY = nwY - startTileY * TILE_SIZE;

  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  // Fill with neutral gray so any failed tiles look intentional (not black).
  ctx.fillStyle = "#334155";
  ctx.fillRect(0, 0, outW, outH);

  // Fetch tiles in parallel, with a modest concurrency cap so we don't nuke
  // the backend or mobile networks.
  const jobs = [];
  for (let tx = startTileX; tx <= endTileX; tx++) {
    for (let ty = startTileY; ty <= endTileY; ty++) {
      jobs.push({ tx, ty });
    }
  }
  const total = jobs.length;
  let done = 0;
  const CONCURRENCY = 8;
  let cursor = 0;
  async function worker() {
    while (cursor < jobs.length) {
      const idx = cursor++;
      const { tx, ty } = jobs[idx];
      const url = buildTileUrl(base, tx, ty, zoom);
      const img = await fetchTileImage(url);
      if (img) {
        const dx = (tx - startTileX) * TILE_SIZE - offsetX;
        const dy = (ty - startTileY) * TILE_SIZE - offsetY;
        ctx.drawImage(img, dx, dy);
      }
      done++;
      if (onProgress) onProgress(done, total);
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, jobs.length) }, worker));

  // Draw pins on top.
  const dotR = zoom >= 17 ? 6 : zoom >= 15 ? 5 : 4;
  for (const p of points) {
    const px = lonToPixelX(p.lng, zoom) - nwX;
    const py = latToPixelY(p.lat, zoom) - nwY;
    if (px < -20 || py < -20 || px > outW + 20 || py > outH + 20) continue;
    ctx.beginPath();
    ctx.arc(px, py, dotR, 0, Math.PI * 2);
    ctx.fillStyle = p.color || "#10b981";
    ctx.fill();
    ctx.lineWidth = 1.8;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();
  }

  // Draw ID labels above pins (skip if unchecked or too many pins at low zoom).
  if (showIdLabels) {
    // At very low zoom labels overlap into unreadable ink — skip.
    const labelZoomOK = zoom >= 15;
    if (labelZoomOK) {
      ctx.font = "bold 10px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      for (const p of points) {
        if (!p.cid) continue;
        const px = lonToPixelX(p.lng, zoom) - nwX;
        const py = latToPixelY(p.lat, zoom) - nwY;
        if (px < -40 || py < -40 || px > outW + 40 || py > outH + 40) continue;
        const text = String(p.cid);
        const textW = ctx.measureText(text).width;
        const boxW = textW + 8;
        const boxH = 14;
        const bx = Math.round(px - boxW / 2);
        const by = Math.round(py - dotR - boxH - 2);
        ctx.fillStyle = "rgba(15,23,42,0.88)";
        roundRect(ctx, bx, by, boxW, boxH, 4);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.7)";
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.fillStyle = "#ffffff";
        ctx.fillText(text, px, by + boxH / 2 + 0.5);
      }
    }
  }

  return canvas;
}

// Download a canvas as JPG (best-quality lossy).
export function downloadCanvasAsJpg(canvas, filename) {
  const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Download a canvas as PDF (fits page to canvas aspect ratio).
export async function downloadCanvasAsPdf(canvas, filename) {
  const { jsPDF } = await import("jspdf");
  const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
  const w = canvas.width;
  const h = canvas.height;
  const orient = w >= h ? "landscape" : "portrait";
  // Use points (pt) and let jsPDF scale — 1pt = 1/72 inch.
  const pdf = new jsPDF({ orientation: orient, unit: "pt", format: [w, h] });
  pdf.addImage(dataUrl, "JPEG", 0, 0, w, h);
  pdf.save(filename);
}

// Compose a nice default filename based on scope.
export function defaultExportFilename(prefix, ext) {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  return `${prefix}-${stamp}.${ext}`;
}
